import { useEffect } from 'react';

import { RefreshCwIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { cn } from '@/shared/utils';

import Loading from '@/components/ui/loading';
import useOngoingSwedishMatches from '@/hooks/home/useOngoingSwedishMatches';

import ErrorState from './ErrorState';
import ScorePill from './ScorePill';
import TeamColumn from './TeamColumn';

const liveHost = import.meta.env.VITE_LIVE_HOST || ''; // fallback to relative path with current domain

const buildLiveUrl = (match = {}) => {
  const centerUuid =
    match.centerUuid ?? match.center_uuid ?? match?.center?.uuid ?? null;
  const leagueId = match.leagueId ?? match.matchId ?? match.id ?? null;

  if (!centerUuid || !leagueId) return null;

  return `${liveHost}/bowlingcenters/${centerUuid}/swedishleague/${leagueId}`;
};

const OngoingSection = () => {
  const { ongoingMatches, isLoading, hasError, refresh } =
    useOngoingSwedishMatches();

  const shouldShowBanner = !ongoingMatches.length && !isLoading && !hasError;

  useEffect(() => {
    if (ongoingMatches.length > 0) {
      refresh();
    }
  }, [ongoingMatches.length, refresh]);

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between text-sm uppercase text-white/60">
        <span>Ongoing Games</span>
        <span className="flex items-center gap-2">
          {ongoingMatches.length > 0 && (
            <span className="text-white/40">{ongoingMatches.length} live</span>
          )}
          <button
            type="button"
            onClick={refresh}
            className="text-xs font-semibold uppercase text-primary transition hover:text-primary/70"
          >
            <RefreshCwIcon size={20} />
          </button>
        </span>
      </div>

      {isLoading && (
        <div className="flex justify-center h-48 items-center">
          <Loading />
        </div>
      )}

      {!isLoading && hasError && (
        <ErrorState onRetry={refresh} message="Couldn't load live games" />
      )}

      {shouldShowBanner && (
        <div className="overflow-hidden rounded-[32px] shadow-xl">
          <img
            src="/images/banner-swedish.png"
            alt="Swedish league banner"
            className="h-auto w-full object-cover"
            loading="lazy"
          />
        </div>
      )}

      {!isLoading && !hasError && !shouldShowBanner && (
        <div className="space-y-1">
          {ongoingMatches.map((match) => (
            <Link
              key={match.id}
              to={buildLiveUrl(match)}
              reloadDocument
              className={cn(
                'flex flex-col',
                'w-full rounded-xl border border-white/15 bg-white/5 p-2 pb-1 text-left transition-all',
                'hover:-translate-y-0.5 hover:border-primary/60 hover:bg-white/10',
                'disabled:cursor-not-allowed disabled:opacity-70'
              )}
            >
              <div className="flex items-center justify-between text-[11px] uppercase text-emerald-200">
                <span>Live</span>
                <span className="text-white/70">
                  {match.centerName || 'Unknown center'}
                </span>
              </div>

              <div className="flex items-center justify-between gap-3">
                <TeamColumn
                  team={match.homeTeam}
                  variant="home"
                  iconSide="right"
                />
                <div className="flex items-center gap-2 text-2xl font-black text-white">
                  <ScorePill
                    value={match.homeTeam.score}
                    highlight={match.homeTeam.isWinner}
                  />
                  <span className="text-white/50">-</span>
                  <ScorePill
                    value={match.awayTeam.score}
                    highlight={match.awayTeam.isWinner}
                  />
                </div>
                <TeamColumn
                  team={match.awayTeam}
                  align="right"
                  variant="away"
                  iconSide="left"
                />
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
};

export default OngoingSection;
